/**
 * Panier de l'ecran de vente : un reducteur pur (aucune date, aucun hasard,
 * aucun acces au stockage : tout arrive dans l'action), le calcul des lignes
 * et des totaux en cents, et la construction du PanierAEncaisser du contrat
 * (vente/contrat.js).
 *
 * Regles retenues (PR 1.5a) :
 *   - sale_uuid (UUID v4) et created_at sont poses a la PREMIERE ligne, puis
 *     gardes tant que le panier vit (retour du paiement compris). Un panier
 *     vide (annule, ou dernieres lignes supprimees) les oublie : la vente
 *     suivante a les siens.
 *   - Un article au poids ne s'ajoute que par son etiquette (prix integre).
 *   - Un article deja dans le panier, rescanne, ajoute 1 a sa ligne (meme
 *     article, a l'unite, sans prix modifie ni rabais en dollars) ; sinon une
 *     nouvelle ligne.
 *   - Un seul rabais par ligne. Rabais en % : recalcule si la quantite ou le
 *     prix change. Rabais en $ : retire si la quantite ou le prix change (le
 *     plafond autorise ne vaudrait plus), avec un avis a l'ecran.
 *   - Rabais sur la vente : remplace les rabais de ligne, et se repartit sur
 *     TOUTES les lignes au prorata de leur montant (plus forts restes : aucun
 *     cent perdu). En % : il suit le panier (une ligne ajoutee en profite). En
 *     $ : retire des que le panier change, avec un avis.
 *   - Taxes : domain/tax.js, methode LIGHTSPEED_SOMME_NON_ARRONDIE.
 */
import {
    CONTRAT_VENTE_VERSION, METHODES_TAXES, MODES_LIGNE, PORTEES_RABAIS, TAX_CODES, TYPES_RABAIS,
} from './contrat';
import { pourcentDe, repartirAuProrata } from '../domain/money';
import { taxesLigne, taxesVente } from '../domain/tax';

export const QTE_MAX = 9999;
export const PRIX_MAX_CENTS = 9999999;

export const ACTIONS_PANIER = Object.freeze({
    AJOUTER: 'AJOUTER',
    AJOUTER_POIDS: 'AJOUTER_POIDS',
    QUANTITE: 'QUANTITE',
    SUPPRIMER: 'SUPPRIMER',
    RABAIS_LIGNE: 'RABAIS_LIGNE',
    RABAIS_VENTE: 'RABAIS_VENTE',
    RETIRER_RABAIS: 'RETIRER_RABAIS',
    PRIX: 'PRIX',
    ANNULER: 'ANNULER',
    AVIS_LU: 'AVIS_LU',
});

export const AVIS = Object.freeze({
    RABAIS_LIGNE_RETIRE: 'Rabais en dollars retiré : la quantité ou le prix a changé. Refaites le rabais si besoin.',
    RABAIS_VENTE_RETIRE: 'Rabais sur la vente retiré : le panier a changé. Refaites le rabais si besoin.',
});

export const PANIER_VIDE = Object.freeze({
    sale_uuid: null,
    created_at: null,
    lignes: Object.freeze([]),
    rabaisVente: null,
    avis: null,
    derniere: null,
    touche: 0,
    prochainId: 1,
});

/** Article du catalogue vendable : cle, nom, prix entier >= 0, code de taxe connu. */
export function articleVendable(article) {
    return Boolean(article
        && typeof article.productcode === 'string' && article.productcode !== ''
        && typeof article.name === 'string'
        && Number.isSafeInteger(article.price_cents) && article.price_cents >= 0
        && TAX_CODES.includes(article.tax_code));
}

/**
 * Article au poids : il ne se vend que par son etiquette a prix integre
 * (AJOUTER_POIDS), jamais a l'unite au prix du catalogue.
 */
export function estAuPoids(article) {
    return Boolean(article && article.weight);
}

/** Prix au kilo d'un article au poids (catalogue), sinon son prix. */
function prixAuPoids(article) {
    const p = article.weight && article.weight.price_per_unit_cents;
    return Number.isSafeInteger(p) && p >= 0 ? p : article.price_cents;
}

function nouvelleLigne(state, article, champs) {
    return {
        id: state.prochainId,
        productcode: article.productcode,
        lightspeed_product_id: article.lightspeed_id === undefined || article.lightspeed_id === null
            ? null
            : String(article.lightspeed_id),
        name: article.name,
        sku: typeof article.sku === 'string' ? article.sku : null,
        tax_code: article.tax_code,
        override: null,
        rabais: null,
        ...champs,
    };
}

/** Le panier a change : un rabais sur la vente en dollars ne vaut plus. */
function panierChange(state) {
    if (state.rabaisVente && state.rabaisVente.type === TYPES_RABAIS.MONTANT) {
        return { ...state, rabaisVente: null, avis: AVIS.RABAIS_VENTE_RETIRE };
    }
    return state;
}

/** Quantite ou prix d'une ligne change : un rabais de ligne en dollars ne vaut plus. */
function ligneChange(ligne, avis) {
    if (ligne.rabais && ligne.rabais.type === TYPES_RABAIS.MONTANT) {
        avis.texte = AVIS.RABAIS_LIGNE_RETIRE;
        return { ...ligne, rabais: null };
    }
    return ligne;
}

function debut(state, action) {
    if (state.sale_uuid) return state;
    return { ...state, sale_uuid: action.uuid, created_at: action.at };
}

function majLigne(state, id, transforme) {
    const avis = { texte: null };
    const lignes = state.lignes.map((l) => (l.id === id ? transforme(l, avis) : l));
    const next = panierChange({ ...state, lignes, derniere: id, touche: state.touche + 1 });
    return avis.texte && !next.avis ? { ...next, avis: avis.texte } : next;
}

function videSiRienAVendre(state) {
    return state.lignes.length === 0 ? { ...PANIER_VIDE, prochainId: state.prochainId, touche: state.touche, avis: state.avis } : state;
}

/**
 * @param {typeof PANIER_VIDE} state
 * @param {object} action une valeur de ACTIONS_PANIER, et ses donnees
 */
export function panierReducer(state, action) {
    const s = state || PANIER_VIDE;
    const sansAvis = s.avis ? { ...s, avis: null } : s;
    switch (action && action.type) {
    case ACTIONS_PANIER.AJOUTER: {
        const { article, scanned_code: scannedCode = null } = action;
        if (!articleVendable(article) || estAuPoids(article)) return s;
        const existante = sansAvis.lignes.find((l) => l.productcode === article.productcode && l.mode === MODES_LIGNE.UNITE
            && !l.override && !(l.rabais && l.rabais.type === TYPES_RABAIS.MONTANT) && l.qty < QTE_MAX);
        const base = debut(sansAvis, action);
        if (existante) return majLigne(base, existante.id, (l) => ({ ...l, qty: l.qty + 1 }));
        const ligne = nouvelleLigne(base, article, {
            scanned_code: scannedCode,
            mode: MODES_LIGNE.UNITE,
            qty: 1,
            weight_g: null,
            catalog_price_cents: article.price_cents,
            unit_price_cents: article.price_cents,
        });
        return panierChange({ ...base, lignes: [...base.lignes, ligne], derniere: ligne.id, touche: base.touche + 1, prochainId: base.prochainId + 1 });
    }
    case ACTIONS_PANIER.AJOUTER_POIDS: {
        const { article, priceCents, scanned_code: scannedCode = null } = action;
        if (!articleVendable(article) || !Number.isSafeInteger(priceCents) || priceCents <= 0) return s;
        const base = debut(sansAvis, action);
        const auKg = prixAuPoids(article);
        const ligne = nouvelleLigne(base, article, {
            scanned_code: scannedCode,
            mode: MODES_LIGNE.POIDS,
            qty: 1,
            weight_g: null,
            catalog_price_cents: auKg,
            unit_price_cents: auKg,
            label_price_cents: priceCents,
            unit: (article.weight && article.weight.unit) || 'kg',
        });
        return panierChange({ ...base, lignes: [...base.lignes, ligne], derniere: ligne.id, touche: base.touche + 1, prochainId: base.prochainId + 1 });
    }
    case ACTIONS_PANIER.QUANTITE: {
        const ligne = sansAvis.lignes.find((l) => l.id === action.id);
        const { qty } = action;
        if (!ligne || ligne.mode !== MODES_LIGNE.UNITE || !Number.isSafeInteger(qty) || qty < 1 || qty > QTE_MAX
            || qty === ligne.qty) {
            return s;
        }
        return majLigne(sansAvis, ligne.id, (l, avis) => ({ ...ligneChange(l, avis), qty }));
    }
    case ACTIONS_PANIER.SUPPRIMER: {
        if (!sansAvis.lignes.some((l) => l.id === action.id)) return s;
        const lignes = sansAvis.lignes.filter((l) => l.id !== action.id);
        return videSiRienAVendre(panierChange({ ...sansAvis, lignes, derniere: null }));
    }
    case ACTIONS_PANIER.RABAIS_LIGNE: {
        const ligne = sansAvis.lignes.find((l) => l.id === action.id);
        if (!ligne || sansAvis.rabaisVente || !action.rabais) return s;
        return { ...sansAvis, lignes: sansAvis.lignes.map((l) => (l.id === ligne.id ? { ...l, rabais: action.rabais } : l)), derniere: ligne.id };
    }
    case ACTIONS_PANIER.RABAIS_VENTE: {
        if (sansAvis.lignes.length === 0 || !action.rabais) return s;
        return { ...sansAvis, rabaisVente: action.rabais, lignes: sansAvis.lignes.map((l) => (l.rabais ? { ...l, rabais: null } : l)) };
    }
    case ACTIONS_PANIER.RETIRER_RABAIS: {
        if (action.id === undefined || action.id === null) return { ...sansAvis, rabaisVente: null };
        return { ...sansAvis, lignes: sansAvis.lignes.map((l) => (l.id === action.id ? { ...l, rabais: null } : l)) };
    }
    case ACTIONS_PANIER.PRIX: {
        const ligne = sansAvis.lignes.find((l) => l.id === action.id);
        if (!ligne || ligne.mode !== MODES_LIGNE.UNITE) return s;
        const { override } = action;
        if (override && (!Number.isSafeInteger(override.prix_cents) || override.prix_cents < 1
            || override.prix_cents > PRIX_MAX_CENTS)) {
            return s;
        }
        const retour = !override || override.prix_cents === ligne.catalog_price_cents;
        return majLigne(sansAvis, ligne.id, (l, avis) => ({
            ...ligneChange(l, avis),
            override: retour ? null : override,
            unit_price_cents: retour ? l.catalog_price_cents : override.prix_cents,
        }));
    }
    case ACTIONS_PANIER.ANNULER:
        return { ...PANIER_VIDE, prochainId: s.prochainId, touche: s.touche };
    case ACTIONS_PANIER.AVIS_LU:
        return sansAvis;
    default:
        return s;
    }
}

function montantBrut(ligne) {
    return ligne.mode === MODES_LIGNE.POIDS ? ligne.label_price_cents : ligne.unit_price_cents * ligne.qty;
}

function rabaisDeLigne(r, portee, montant) {
    if (!r || montant <= 0) return null;
    return {
        type: r.type,
        valeur: r.valeur,
        portee,
        montant_cents: montant,
        par: r.par,
        autorise_par: r.autorise_par || null,
    };
}

/** Montant d'un rabais saisi sur un montant brut (sans plafond ni droit). */
export function montantDuRabais({ type, valeur }, brutCents) {
    if (type === TYPES_RABAIS.POURCENT) return pourcentDe(brutCents, valeur);
    if (type === TYPES_RABAIS.MONTANT) return Math.min(valeur, brutCents);
    throw new RangeError(`type de rabais inconnu : ${type}`);
}

/**
 * Lignes du contrat (LigneVente) et totaux (TotauxVente), en cents.
 * @returns {{ lignes: object[], totals: object }}
 */
export function calculerPanier(state) {
    const s = state || PANIER_VIDE;
    const bruts = s.lignes.map(montantBrut);
    let rabais;
    if (s.rabaisVente) {
        const totalBrut = bruts.reduce((a, b) => a + b, 0);
        const parts = repartirAuProrata(montantDuRabais(s.rabaisVente, totalBrut), bruts);
        rabais = parts.map((m) => rabaisDeLigne(s.rabaisVente, PORTEES_RABAIS.VENTE, m));
    } else {
        rabais = s.lignes.map((l, i) => (l.rabais
            ? rabaisDeLigne(l.rabais, PORTEES_RABAIS.LIGNE, montantDuRabais(l.rabais, bruts[i]))
            : null));
    }
    const lignes = s.lignes.map((l, i) => {
        const gross = bruts[i];
        const discount = rabais[i] ? rabais[i].montant_cents : 0;
        const net = gross - discount;
        const { tps_cents: tps, tvq_cents: tvq } = taxesLigne(net, l.tax_code);
        return {
            line_no: i + 1,
            productcode: l.productcode,
            lightspeed_product_id: l.lightspeed_product_id,
            name: l.name,
            sku: l.sku,
            scanned_code: l.scanned_code,
            mode: l.mode,
            qty: l.qty,
            weight_g: l.weight_g,
            unit_price_cents: l.unit_price_cents,
            catalog_price_cents: l.catalog_price_cents,
            override: l.override,
            discount: rabais[i],
            gross_cents: gross,
            discount_cents: discount,
            net_cents: net,
            tax_code: l.tax_code,
            tps_cents: tps,
            tvq_cents: tvq,
            line_total_cents: net + tps + tvq,
        };
    });
    const somme = (k) => lignes.reduce((a, l) => a + l[k], 0);
    const taxes = taxesVente(lignes.map((l) => ({ base: l.net_cents, tax_code: l.tax_code })));
    const subtotal = somme('net_cents');
    const totals = {
        gross_cents: somme('gross_cents'),
        discount_cents: somme('discount_cents'),
        subtotal_cents: subtotal,
        tps_cents: taxes.tps_cents,
        tvq_cents: taxes.tvq_cents,
        tax_cents: taxes.tax_cents,
        total_cents: subtotal + taxes.tax_cents,
        item_count: somme('qty'),
    };
    return { lignes, totals };
}

/**
 * PanierAEncaisser (contrat) : fige, a passer a onEncaisser apres
 * verifierPanier.
 * @param {typeof PANIER_VIDE} state
 * @param {{ terminal_id: number, store_id: number, employee_id: number, employee_display_name: string,
 *   catalog_snapshot_id: string|null }} contexte
 */
export function construirePanier(state, contexte) {
    const { lignes, totals } = calculerPanier(state);
    return {
        contrat_version: CONTRAT_VENTE_VERSION,
        sale_uuid: state.sale_uuid,
        terminal_id: contexte.terminal_id,
        store_id: contexte.store_id,
        employee_id: contexte.employee_id,
        employee_display_name: contexte.employee_display_name,
        catalog_snapshot_id: contexte.catalog_snapshot_id || null,
        created_at: state.created_at,
        methode_taxes: METHODES_TAXES.LIGHTSPEED_SOMME_NON_ARRONDIE,
        lines: lignes,
        totals,
    };
}

function rabaisSaisi(d) {
    return { type: d.type, valeur: d.valeur, par: d.par, autorise_par: d.autorise_par || null };
}

/**
 * Etat de l'ecran a partir d'un PanierAEncaisser (retour du paiement) : memes
 * lignes, meme sale_uuid, meme created_at, memes rabais et prix modifies.
 */
export function etatDepuisPanier(panier) {
    if (!panier || !Array.isArray(panier.lines) || panier.lines.length === 0) return PANIER_VIDE;
    let rabaisVente = null;
    const lignes = panier.lines.map((l, i) => {
        if (l.discount && l.discount.portee === PORTEES_RABAIS.VENTE && !rabaisVente) rabaisVente = rabaisSaisi(l.discount);
        const ligne = {
            id: i + 1,
            productcode: l.productcode,
            lightspeed_product_id: l.lightspeed_product_id,
            name: l.name,
            sku: l.sku,
            tax_code: l.tax_code,
            override: l.override || null,
            rabais: l.discount && l.discount.portee === PORTEES_RABAIS.LIGNE ? rabaisSaisi(l.discount) : null,
            scanned_code: l.scanned_code,
            mode: l.mode,
            qty: l.qty,
            weight_g: l.weight_g,
            catalog_price_cents: l.catalog_price_cents,
            unit_price_cents: l.unit_price_cents,
        };
        if (l.mode === MODES_LIGNE.POIDS) {
            ligne.label_price_cents = l.gross_cents;
            ligne.unit = 'kg';
        }
        return ligne;
    });
    return {
        ...PANIER_VIDE,
        sale_uuid: panier.sale_uuid,
        created_at: panier.created_at,
        lignes,
        rabaisVente,
        prochainId: lignes.length + 1,
    };
}
